// EntityType Configuration - UNIFIED APPROACH
// Manages entity types for audit logging and linking (Document, Revision, CAPA, Risk Assessment, etc.)
// Based on Prisma EntityType model schema

import { createTrackingConfig } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';

export const entityTypeConfig = {
  entityName: 'EntityType',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/entity-type',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // EntityType specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // Entity types typically don't have lookup dependencies
      // but you could add categories, modules, etc.
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '600px', // Standard width for entity types
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle // From app config: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'entityTypeService',
    methods: {
      getAll: 'getAllEntityTypes',
      getById: 'getEntityTypeById', 
      create: 'createEntityType',
      update: 'updateEntityType',
      delete: 'deleteEntityType',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'text',
      table: { header: 'ID', width: '50px', sortable: true },
      filterable: true
    },
    { 
      name: 'name', 
      label: 'Entity Type Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter entity type name (e.g., Document, CAPA)',
      table: { header: 'Entity Type', width: '200px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
        pattern: /^[A-Za-z\s&\-'.()]+$/,
        message: 'Entity type name must contain only letters, spaces, and common punctuation'
      }
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Entity type is active and available for use',
      description: 'Inactive entity types cannot be used for audit logging or linking'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (1-999)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { 
        required: false,
        min: 1,
        max: 999,
        integer: true,
        message: 'Display order must be between 1 and 999'
      },
      helperText: 'Controls the order in dropdown lists and displays (lower numbers appear first)'
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Table configuration for proper rendering and action button widths
  table: {
    variant: 'default',
    density: 'normal',
    showSelection: true,
    showPagination: true,
    showSearch: true,
    showFilters: true,
    showColumnToggle: true,
    defaultSort: { field: 'record_order', direction: 'asc' }
  },

  // Filter Configuration
  filterableFields: ['name', 'isActive'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['id', 'name', 'isActive']
    },
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false], // Active entity types first
    record_order: 'asc' // Ascending order by default
  },

  // Messages Configuration
  messages: {
    loading: 'Loading entity types...',
    noData: 'No entity types found. Click "Add Entity Type" to create the first entity type.',
    loadError: 'Failed to load entity types',
    saveSuccess: 'Entity type saved successfully!',
    saveError: 'Failed to save entity type',
    deleteSuccess: 'Entity type deleted successfully!',
    deleteError: 'Failed to delete entity type',
    deleteConfirm: 'Are you sure you want to delete this entity type? This action cannot be undone and may affect audit logs, CAPA records, risk assessments, and deviations that reference this entity type.',
    activateSuccess: 'Entity type activated successfully!',
    deactivateSuccess: 'Entity type deactivated successfully!'
  },

  // Form Layout Configuration
  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'Entity Type Information',
        icon: '🏷️',
        columns: 2,
        fields: [
          { name: 'name' },
          { name: 'isActive' },
          { name: 'record_order' }
        ]
      }
    ]
  },

  // Form sections configuration (for form rendering)
  formSections: [
    {
      id: 'basic',
      title: 'Entity Type Information',
      icon: '🏷️',
      columns: 2,
      fields: [
        { name: 'id' },
        { name: 'name' },
        { name: 'isActive' },
        { name: 'record_order' }
      ]
    }
  ],

  // Form layout presets for different view modes
  formLayoutPresets: {
    compact: {
      sections: [
        {
          title: 'Basic Information',
          fields: ['id', 'name', 'isActive']
        }
      ]
    },
    detailed: {
      sections: [
        {
          title: 'Basic Information',
          fields: ['id', 'name', 'isActive', 'record_order']
        }
      ]
    }
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('EntityType'),

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'Entity Type',
    plural: 'Entity Types',
    addButton: 'Add Entity Type',
    editButton: 'Edit Entity Type',
    deleteButton: 'Delete Entity Type',
    
    // Custom labels for entity types
    pageTitle: 'Entity Type Management',
    pageSubtitle: 'Manage entity types for audit logging and system linking',
    searchPlaceholder: 'Search entity types by name...',
    
    // Form labels
    basicInfoSection: 'Entity Type Information',
    
    // Action labels
    activateEntityType: 'Activate Entity Type',
    deactivateEntityType: 'Deactivate Entity Type',
    reorderEntityTypes: 'Reorder Entity Types'
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    // Remove system fields that should not be updated
    'createdAt',
    'updatedAt', 
    'createdById',
    'updatedById',
    // Remove all relation fields
    'auditLogs',
    'capa',
    'deviations',
    'riskAssessments'
  ],

  // Fields to keep even if they're in system fields to remove
  fieldsToKeep: ['id'],

  // Export filename for data exports
  exportFileName: 'entity-types',

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    
    // Convert id to integer (required field)
    if (transformed.id !== undefined) {
      if (transformed.id === '' || transformed.id === null) {
        // Remove empty id field entirely
        delete transformed.id;
      } else {
        // Convert to integer
        const numValue = parseInt(transformed.id, 10);
        if (!isNaN(numValue)) {
          transformed.id = numValue;
        } else {
          delete transformed.id; // Remove if not a valid number
        }
      }
    }
    
    // Convert record_order to integer or remove if empty
    if (transformed.record_order !== undefined) {
      if (transformed.record_order === '' || transformed.record_order === null) {
        // Remove empty record_order field entirely
        delete transformed.record_order;
      } else {
        // Convert to integer
        const numValue = parseInt(transformed.record_order, 10);
        if (!isNaN(numValue)) {
          transformed.record_order = numValue;
        } else {
          delete transformed.record_order; // Remove if not a valid number
        }
      }
    }
    
    return transformed;
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
